import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import ExclamationTriangleIcon from "@/assets/icons/admin/exclamation-triangle.svg?react";
import FileBarChartIcon from "@/assets/icons/admin/file-bar-chart.svg?react";
import MagnetIcon from "@/assets/icons/admin/magnet.svg?react";
import {
  AdminLeadsTable,
  type AdminLeadRow,
} from "@/components/admin/admin-leads-table";
import { AdminStatCard } from "@/components/admin/admin-stat-card";
import { AdminVisitsTable } from "@/components/admin/admin-visits-table";
import { AdminHeader } from "@/components/layout/admin-header";
import { adminApi, type AdminScoreFilter } from "@/lib/admin-api";
import {
  formatCompactGbp,
  mapLeadApiToRow,
  mapVisitApiToRow,
  type AdminVisitRow,
} from "@/lib/admin-dashboard";
import {
  adminDashboardQueryKey,
  adminDashboardRootKey,
  adminVisitsQueryKey,
} from "@/lib/admin-query-keys";
import { toastApiError } from "@/lib/toast";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 300;

type OverviewTab = "leads" | "presentation" | "visits";

export function AdminHomePage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<OverviewTab>("leads");

  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsSearchInput, setLeadsSearchInput] = useState("");
  const [leadsSearch, setLeadsSearch] = useState("");
  const [scoreFilter, setScoreFilter] = useState<AdminScoreFilter>("all");

  const [presentationPage, setPresentationPage] = useState(1);
  const [presentationSearchInput, setPresentationSearchInput] = useState("");
  const [presentationSearch, setPresentationSearch] = useState("");
  const [presentationScoreFilter, setPresentationScoreFilter] =
    useState<AdminScoreFilter>("all");

  const [visitsPage, setVisitsPage] = useState(1);
  const [visitsSearchInput, setVisitsSearchInput] = useState("");
  const [visitsSearch, setVisitsSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLeadsSearch(leadsSearchInput.trim());
      setLeadsPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [leadsSearchInput]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPresentationSearch(presentationSearchInput.trim());
      setPresentationPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [presentationSearchInput]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVisitsSearch(visitsSearchInput.trim());
      setVisitsPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [visitsSearchInput]);

  const dashboardQuery = useQuery({
    queryKey: adminDashboardQueryKey(leadsPage, leadsSearch, scoreFilter),
    queryFn: () =>
      adminApi.getDashboard({
        page: leadsPage,
        pageSize: PAGE_SIZE,
        search: leadsSearch,
        score: scoreFilter,
        viewMode: "visitor",
      }),
    placeholderData: keepPreviousData,
  });

  const presentationQuery = useQuery({
    queryKey: adminDashboardQueryKey(
      presentationPage,
      presentationSearch,
      presentationScoreFilter,
      "presenter",
    ),
    queryFn: () =>
      adminApi.getDashboard({
        page: presentationPage,
        pageSize: PAGE_SIZE,
        search: presentationSearch,
        score: presentationScoreFilter,
        viewMode: "presenter",
      }),
    placeholderData: keepPreviousData,
    enabled: tab === "presentation",
  });

  const visitsQuery = useQuery({
    queryKey: adminVisitsQueryKey(visitsPage, visitsSearch),
    queryFn: () =>
      adminApi.getVisits({
        page: visitsPage,
        pageSize: PAGE_SIZE,
        search: visitsSearch,
      }),
    placeholderData: keepPreviousData,
    enabled: tab === "visits",
  });

  useEffect(() => {
    if (dashboardQuery.isError) {
      toastApiError(dashboardQuery.error, "Unable to load dashboard data.");
    }
  }, [dashboardQuery.isError, dashboardQuery.error]);

  useEffect(() => {
    if (visitsQuery.isError) {
      toastApiError(visitsQuery.error, "Unable to load visits.");
    }
  }, [visitsQuery.isError, visitsQuery.error]);

  useEffect(() => {
    if (presentationQuery.isError) {
      toastApiError(
        presentationQuery.error,
        "Unable to load presentation leads.",
      );
    }
  }, [presentationQuery.isError, presentationQuery.error]);

  const deleteMutation = useMutation({
    mutationFn: (leadId: string) => adminApi.deleteLead(leadId),
    onSuccess: async () => {
      toast.success("Lead deleted.");
      await queryClient.invalidateQueries({ queryKey: adminDashboardRootKey });
    },
    onError: (error) => {
      toastApiError(error, "Unable to delete lead.");
    },
  });

  const stats = dashboardQuery.data?.stats ?? null;
  const leads =
    dashboardQuery.data?.leads?.items?.map(mapLeadApiToRow) ??
    ([] as AdminLeadRow[]);
  const leadsTotal = dashboardQuery.data?.leads?.total ?? 0;
  const leadsTotalPages = dashboardQuery.data?.leads?.totalPages ?? 1;
  const leadsLoading = dashboardQuery.isLoading || dashboardQuery.isFetching;

  const visits =
    visitsQuery.data?.items?.map(mapVisitApiToRow) ?? ([] as AdminVisitRow[]);
  const visitsTotal = visitsQuery.data?.total ?? 0;
  const visitsTotalPages = visitsQuery.data?.totalPages ?? 1;
  const visitsLoading = visitsQuery.isLoading || visitsQuery.isFetching;

  const presentationLeads =
    presentationQuery.data?.leads?.items?.map(mapLeadApiToRow) ??
    ([] as AdminLeadRow[]);
  const presentationTotal = presentationQuery.data?.leads?.total ?? 0;
  const presentationTotalPages = presentationQuery.data?.leads?.totalPages ?? 1;
  const presentationLoading =
    presentationQuery.isLoading || presentationQuery.isFetching;

  function handleScoreFilterChange(next: AdminScoreFilter) {
    setScoreFilter(next);
    setLeadsPage(1);
  }

  function handlePresentationScoreFilterChange(next: AdminScoreFilter) {
    setPresentationScoreFilter(next);
    setPresentationPage(1);
  }

  async function handleDeleteLead(lead: AdminLeadRow) {
    await deleteMutation.mutateAsync(lead.id);
  }

  return (
    <div className="flex min-h-full flex-col bg-[#091424]">
      <AdminHeader
        title="Overview"
        description="Aggregate data from all Trapped Capacity Index (TCI) calculations."
      />

      <div className="flex flex-1 flex-col gap-8 px-8 pb-8 pt-8">
        <section className="grid gap-4 md:grid-cols-3">
          <AdminStatCard
            label="Total Interaction"
            value={String(
              stats?.totalInteractions ?? (dashboardQuery.isLoading ? "…" : 0),
            )}
            meta={`+${stats?.interactionsThisWeek ?? 0} this week`}
            metaTone="positive"
            icon={<FileBarChartIcon aria-hidden className="size-4" />}
          />
          <AdminStatCard
            label="Totals Leads"
            value={String(
              stats?.totalLeads ?? (dashboardQuery.isLoading ? "…" : 0),
            )}
            meta={`+${stats?.leadsThisWeek ?? 0} this week`}
            metaTone="positive"
            icon={<MagnetIcon aria-hidden className="size-4" />}
          />
          <AdminStatCard
            label="Total Trapped Margin"
            value={
              stats
                ? formatCompactGbp(stats.totalTrappedMargin)
                : dashboardQuery.isLoading
                  ? "…"
                  : "£0"
            }
            meta="Total across leads"
            icon={<ExclamationTriangleIcon aria-hidden className="size-4" />}
          />
        </section>

        <div className="flex items-center gap-2 border-b border-white/5">
          {(
            [
              { id: "leads", label: "Leads" },
              { id: "presentation", label: "Presentation" },
              { id: "visits", label: "Visits" },
            ] as const
          ).map((item) => {
            const isActive = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                aria-selected={isActive}
                role="tab"
                className={cn(
                  "relative px-4 py-3 text-sm font-bold transition-colors",
                  isActive ? "text-white" : "text-[#9194a1] hover:text-white",
                )}
                onClick={() => setTab(item.id)}
              >
                {item.label}
                {isActive ? (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 bg-[#38bdf8]" />
                ) : null}
              </button>
            );
          })}
        </div>

        {tab === "leads" ? (
          <AdminLeadsTable
            leads={leads}
            totalCount={leadsTotal}
            currentPage={leadsPage}
            totalPages={leadsTotalPages}
            search={leadsSearchInput}
            scoreFilter={scoreFilter}
            isLoading={leadsLoading && leads.length === 0}
            onSearchChange={setLeadsSearchInput}
            onScoreFilterChange={handleScoreFilterChange}
            onPageChange={setLeadsPage}
            onDeleteLead={handleDeleteLead}
          />
        ) : tab === "presentation" ? (
          <AdminLeadsTable
            leads={presentationLeads}
            totalCount={presentationTotal}
            currentPage={presentationPage}
            totalPages={presentationTotalPages}
            search={presentationSearchInput}
            scoreFilter={presentationScoreFilter}
            isLoading={presentationLoading && presentationLeads.length === 0}
            title="Presentation"
            emptyMessage="No presentation leads match your filters."
            searchLabel="Search presentation leads"
            paginationLabel="Presentation pagination"
            onSearchChange={setPresentationSearchInput}
            onScoreFilterChange={handlePresentationScoreFilterChange}
            onPageChange={setPresentationPage}
            onDeleteLead={handleDeleteLead}
          />
        ) : (
          <AdminVisitsTable
            visits={visits}
            totalCount={visitsTotal}
            currentPage={visitsPage}
            totalPages={visitsTotalPages}
            search={visitsSearchInput}
            isLoading={visitsLoading && visits.length === 0}
            onSearchChange={setVisitsSearchInput}
            onPageChange={setVisitsPage}
          />
        )}
      </div>
    </div>
  );
}
